import { useState, useEffect } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { UserButton } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../Api/useApi.ts";
import {
    QuestionCircleOutlined,
    PhoneOutlined,
    SettingOutlined,
} from "@ant-design/icons";
import FeatureCard from "../Components/DashboardComponents/FeatureCard.jsx";
import FormRepliesCard from "../Components/DashboardComponents/FormRepliesCard.jsx";
import FormCard from "../Components/DashboardComponents/FormCard.jsx";
import Workspaces from "../Components/DashboardComponents/Workspaces.jsx";
import { useFormEditorContext } from "../Contexts/FormEditorContext.jsx";
import { useUser } from "@clerk/clerk-react";

import useKeyboardShortcut from "../Components/DashboardComponents/useKeyboardShortcut.jsx";
import CommonLayout from "./CommonLayout.jsx";
import {
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../Components/ui/command.tsx";

import { Separator } from "../Components/ui/separator.tsx";
import { Input } from "../Components/ui/input.tsx";
import { Button } from "../Components/ui/button.tsx";
import clsx from "clsx";
import { PricingModal } from "../Components/PricingModal.tsx";
import { PaymentSuccessModal } from "../Components/PaymentSuccessModal.tsx";
import { Search } from "lucide-react";
import { MobileSheet } from "../Components/DashboardComponents/MobileSheet.tsx";
import { MobileFormCard } from "../Components/DashboardComponents/MobileFormCard.tsx";
import { MobileFormCreateSheet } from "../Components/DashboardComponents/MobileFormCreateSheet.tsx";
import { FormCreateModal } from "../Components/FormCreateModal.tsx";


const DashboardLayout = () => {
    const api = useApi();
    const { user } = useUser();
    const {
        selectedWorkSpace,
        setSelectedWorkSpace,
        setShowPricingModal,
        showPricingModal,
        usersPlan,
        setOpenCalendly,
        showPaymentSuccessModal,
        setShowPaymentSuccessModal,
    } = useFormEditorContext();

    const [workSpaces, setWorkSpaces] = useState([]); //use to map for search
    //const [formList, setFormList] = useState([]);

    const { isFetching, data: formList } = useQuery({
        queryKey: ["formList", selectedWorkSpace],
        queryFn: () => {
            if (!selectedWorkSpace) {
                return;
            }
            return api.revisions.getAllRevisions(selectedWorkSpace);
        },
        staleTime: Infinity,
        onError: (error) => {
            console.error("Error details:", error);
        },
        enabled: !!selectedWorkSpace,
    });

    /* modal states */
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };
    useKeyboardShortcut(["cmd+k", "ctrl+k"], (key, e) => {
        e.preventDefault();

        showModal();
    });

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    /* change logs */
    useEffect(() => {
        const win = window;

        // Initialize Featurebase widget
        const initializeFeaturebase = () => {
            if (typeof win.Featurebase !== "function") {
                win.Featurebase = function () {
                    (win.Featurebase.q = win.Featurebase.q || []).push(
                        arguments,
                    );
                };
            }

            win.Featurebase("initialize_changelog_widget", {
                organization: "Buildform", // Replace this with your Featurebase organization name
                placement: "right", // Choose between right, left, top, bottom placement
                theme: "light", // Choose between dark or light theme
                fullscreenPopup: true, // Optional - Open a fullscreen announcement
                locale: "en",
            });
        };

        // Inject the script dynamically
        const scriptId = "featurebase-sdk";
        if (!document.getElementById(scriptId)) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://do.featurebase.app/js/sdk.js";
            document.body.appendChild(script);

            script.onload = () => {
                initializeFeaturebase();
            };
        } else {
            initializeFeaturebase();
        }
    }, []);

    // Function to manually trigger the changelog popup
    const openChangelogPopup = () => {
        if (window.Featurebase) {
            window.Featurebase("manually_open_changelog_popup"); // Manually trigger the changelog popup
        } else {
            console.error("Featurebase is not initialized yet.");
        }
    };
    const [isCollapsed, setIsCollapsed] = useState(true);

    const selectedWorkSpaceName = workSpaces?.find(
        (workspace) => workspace?._id === selectedWorkSpace,
    )?.name;

    const dashboardSidebar = (
        <div className="w-[320px] h-full bg-white px-5 pt-4 flex flex-col relative scrollbar-none ">
            {/* user nav sec */}
            <PricingModal
                isOpen={showPricingModal}
                onClose={() => setShowPricingModal(false)}
            />
            <PaymentSuccessModal
                isOpen={showPaymentSuccessModal}
                onClose={() => setShowPaymentSuccessModal(false)}
            />
            <UserButton showName={true} afterSignOutUrl="/login" />

            <div onClick={showModal} className="w-full mt-5">
                <button className="inline-flex items-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 border border-input  hover:text-accent-foreground px-4 py-2 relative h-8 w-full justify-start rounded-[0.4rem] bg-[#F4F4F5] text-sm font-normal text-muted-foreground shadow-none sm:pr-12 ">
                    <span className="hidden lg:inline-flex">Search ...</span>
                    <span className="inline-flex lg:hidden">Search...</span>
                    <kbd className="pointer-events-none absolute right-[0.3rem] top-[0.3rem] hidden h-5 select-none items-center gap-1 rounded border bg-[#F4F4F5] px-1.5 text-[10px] font-medium opacity-100 sm:flex">
                        <span className="text-xs">⌘</span>K
                    </kbd>
                </button>
            </div>

            {/* add workspace sec */}
            <Workspaces
                workSpaces={workSpaces}
                setWorkSpaces={setWorkSpaces}
                selectedWorkSpace={selectedWorkSpace}
                setSelectedWorkSpace={setSelectedWorkSpace}
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
            />

            {/* Helps */}

            <div className="flex flex-col items-start justify-center pb-4 mt-auto">
                <p className="mb-2 text-xs font-medium opacity-30">Helps</p>
                <div className="flex flex-col gap-4 text-xs font-semibold">
                    <button className="flex items-center gap-2 text-start">
                        <a
                            href="https://buildform.featurebase.app/help"
                            target="_blank"
                            className="flex items-center gap-2"
                        >
                            <QuestionCircleOutlined />
                            <span>Help Docs</span>
                        </a>
                    </button>

                    <button
                        onClick={() => {
                            setOpenCalendly(true);
                        }}
                        className="flex items-center gap-2 text-start"
                    >
                        <PhoneOutlined /> Talk to us
                    </button>

                    <button
                        onClick={openChangelogPopup}
                        className="flex items-center gap-2 text-start"
                    >
                        <SettingOutlined />
                        Change logs <span id="fb-update-badge"></span>
                    </button>
                </div>
            </div>

            <div
                className={clsx(
                    `flex flex-col gap-3 pt-2 pb-3  hover:cursor-pointer `,
                    usersPlan !== "free"
                        ? "border-none"
                        : "border-t border-zinc-200",
                )}
            >
                {/* Remove comments when the data is available */}
                {/*  <div className="flex flex-col gap-1 ">
                    <div className="flex items-center w-full gap-1">
                        <p className="text-xs font-medium tracking-widest text-zinc-950 ">
                            Unlimited replies remaining
                        </p>
                        <CircleHelp className="text-[#8590A2]" size={10} />
                    </div>
                    <Progress value={80} color="#2563EB" />
                </div>
                <div className="flex flex-col gap-1 ">
                    <div className="flex items-center w-full gap-1">
                        <p className="text-xs font-medium tracking-widest text-zinc-950 ">
                            25 AI statements remaining
                        </p>
                        <CircleHelp className="text-[#8590A2]" size={10} />
                    </div>
                    <Progress value={50} color="#CF9FFF" />
                </div> */}

                {usersPlan === "free" && (
                    <Button
                        className="w-full mx-[0.25px] mt-1 h-10 text-sm font-medium text-white bg-[#191B33] hover:bg-[#191B33] flex items-center gap-2 rounded-lg "
                        onClick={() => {
                            setShowPricingModal(true);
                        }}
                    >
                        Upgrade to PRO
                        <svg
                            width="15"
                            height="19"
                            viewBox="0 0 15 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M4.43205 11.479C5.00785 11.479 5.56007 11.2503 5.96722 10.8431C6.37437 10.436 6.6031 9.88378 6.6031 9.30798C6.6031 8.10956 6.16889 7.57113 5.73468 6.70271C4.80374 4.84169 5.54016 3.18213 7.47152 1.49219C7.90574 3.66324 9.20837 5.74745 10.9452 7.13692C12.6821 8.5264 13.5505 10.1764 13.5505 11.9132C13.5505 12.7115 13.3932 13.502 13.0877 14.2396C12.7822 14.9771 12.3345 15.6472 11.77 16.2117C11.2055 16.7762 10.5354 17.224 9.79784 17.5295C9.06031 17.835 8.26982 17.9922 7.47152 17.9922C6.67323 17.9922 5.88274 17.835 5.14521 17.5295C4.40768 17.224 3.73754 16.7762 3.17306 16.2117C2.60858 15.6472 2.16081 14.9771 1.85531 14.2396C1.54981 13.502 1.39258 12.7115 1.39258 11.9132C1.39258 10.912 1.7686 9.92108 2.261 9.30798C2.261 9.88378 2.48973 10.436 2.89689 10.8431C3.30404 11.2503 3.85625 11.479 4.43205 11.479Z"
                                fill="url(#paint0_linear_7710_2613)"
                                stroke="url(#paint1_linear_7710_2613)"
                                stroke-width="1.30263"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_7710_2613"
                                    x1="7.47152"
                                    y1="1.49219"
                                    x2="7.47152"
                                    y2="17.9922"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#FFC400" />
                                    <stop offset="1" stop-color="#FF9500" />
                                </linearGradient>
                                <linearGradient
                                    id="paint1_linear_7710_2613"
                                    x1="7.47152"
                                    y1="1.49219"
                                    x2="7.47152"
                                    y2="17.9922"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#3A3A3A" />
                                    <stop offset="1" stop-color="#FF9500" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </Button>
                )}
            </div>
        </div>
    );

    const [searchTerm, setSearchTerm] = useState("");
    // Filter form list by title matching the searchTerm (case insensitive)
    const filteredForms = formList?.data?.filter((item) => {
        const title = item?.revision?.form?.title || "";
        return title.toLowerCase().includes(searchTerm.toLowerCase());
    });

    return (
        <>
            <div className="w-full mx-auto overflow-hidden overflow-y-hidden bg-white md:hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <MobileSheet
                            workSpaces={workSpaces}
                            setWorkSpaces={setWorkSpaces}
                            selectedWorkSpace={selectedWorkSpace}
                            setSelectedWorkSpace={setSelectedWorkSpace}
                            isCollapsed={isCollapsed}
                            setIsCollapsed={setIsCollapsed}
                            openChangelogPopup={openChangelogPopup}
                        />
                        <Separator className="w-[1px] h-6 bg-gray-200" />
                        <h1 className="text-sm font-semibold">Dashboard</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* <Gift className="w-6 h-6" />
                        <HelpCircle className="w-6 h-6" /> */}
                        <div className="relative">
                            <UserButton showName={false} />
                        </div>
                    </div>
                </div>
                <Separator className="mb-4" />

                {/* Search Bar */}
                <div className="px-3 mb-6">
                    <div className="relative">
                        <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                        <Input
                            placeholder="Search"
                            className="pl-10 border-0 rounded-lg bg-gray-50"
                            onChange={(e) => setSearchTerm(e.target.value)}
                            value={searchTerm}
                        />
                    </div>
                </div>

                {/* Form Replies Section */}
                <div className="px-3 mb-6">
                    <FormRepliesCard />
                </div>

                {/* Forms Section */}
                <div className="w-full px-3">
                    <h2 className="mb-1 text-lg font-semibold text-start">
                        {selectedWorkSpaceName}
                    </h2>
                    <h3 className="mb-4 text-gray-600 text-start">
                        Recent forms
                    </h3>

                    {/* Form Cards */}
                    <div className="mb-24 space-y-3 ">
                        {filteredForms?.length > 0 ? (
                            filteredForms?.map((item) => (
                                <MobileFormCard
                                    key={item?._id || item?.revision?.form?._id}
                                    item={item}
                                />
                            ))
                        ) : (
                            <p className="text-center text-gray-500">
                                No forms found
                            </p>
                        )}
                    </div>

                    {/* New Form Button */}
                </div>
                {/* Create Form */}
                <div className="fixed bottom-0 w-full p-4 transform rounded-t-[16px] -translate-x-1/2 bg-white border-t shadow-lg border-[#E4E4E7] left-1/2 ">
                    <MobileFormCreateSheet />
                </div>
            </div>

            {/* Desktop View */}
            <CommonLayout
                sidebar={dashboardSidebar}
                dashboardStyles="hidden md:flex"
            >
                {/* main section */}

                <div className="relative w-full h-full px-8 overflow-y-auto transition duration-200 border rounded-[12px] pb-4 no-scrollbar border-zinc-200">
                    <section className="flex flex-col justify-between gap-4 pt-3 lg:items-center lg:flex-row">
                        <div className="flex flex-col gap-4 mt-5">
                            <h1 className="flex flex-col items-center mb-1 text-xl">
                                <p className="text-[2rem] font-bold text-start">
                                    Hey {user?.firstName} 👋
                                </p>
                            </h1>
                            <p className="hidden text-sm text-zinc-500">
                                Find all the forms you have created
                            </p>
                            <p className="hidden text-lg font-medium text-start">
                                My Forms
                            </p>
                        </div>
                        <button
                            type="primary"
                            className="flex items-center justify-center mt-5 px-2 py-2 font-medium text-white bg-[#191B33] rounded-lg hover:bg-zinc-800"
                            //onClick={showCreateModal}
                            onClick={() => setCreateModalOpen(true)}
                        >
                            <PlusOutlined className=" w-3.5 h-3.5 text-base">
                                {" "}
                            </PlusOutlined>
                            <span className="ml-1 text-sm">New Form</span>
                        </button>
                    </section>
                    <div className="grid gap-4 my-6 lg:grid-cols-3">
                        {/* Replies card */}
                        <FormRepliesCard />
                        {/* Feature Card */}
                        <FeatureCard />
                    </div>
                    <p className="mt-6 mb-3 text-sm text-start text-zinc-500">
                        Recent forms
                    </p>
                    <section className="grid  grid-cols-[repeat(auto-fill,255px)] gap-2">
                        <button
                            type="button"
                            aria-haspopup="dialog"
                            aria-expanded="false"
                            aria-controls="radix-:rk:"
                            data-state="closed"
                            //onClick={showCreateModal}
                            onClick={() => setCreateModalOpen(true)}
                            className="h-[12rem] relative border border-zinc-200 border-dashed rounded-2xl hover:scale-[.98] transition duration-500 flex items-center gap-1 flex-col justify-center hover:bg-zinc-100 "
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                                data-slot="icon"
                                className="w-6 h-6"
                            >
                                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"></path>
                            </svg>
                            <p className="text-sm text-gray-700">
                                Create a form
                            </p>
                        </button>
                        <FormCard formList={formList} isFetching={isFetching} />
                    </section>
                </div>
            </CommonLayout>
            <CommandDialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <CommandInput placeholder="Type a command or search..." />
                <CommandList>
                    <CommandEmpty>No results found.</CommandEmpty>
                    <CommandGroup heading="Suggestions">
                        {workSpaces?.map((item) => (
                            <CommandItem key={item?._id}>
                                <button
                                    onClick={() => {
                                        setSelectedWorkSpace(item?._id);
                                        handleCancel();
                                    }}
                                >
                                    {item?.name}
                                </button>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                </CommandList>
            </CommandDialog>

            {/* Create Form modal */}
            <FormCreateModal
                createModalOpen={createModalOpen}
                setCreateModalOpen={setCreateModalOpen}
            />
        </>
    );
};

export default DashboardLayout;


 
      
     
      

